import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import { useMutation } from "react-query";
import usePopup from "../../../hooks/usePopup";
import { voicePromptService } from "../../../services/voiceAssistantService";
import type { PatientSuggestionResponse } from "../../../Types/VoiceAssistant/PatientSuggestionTypes";
import {
  hasSuggestionContent,
  parsePatientSuggestionResponse,
  PATIENT_SUGGESTION_PROMPT,
} from "../../../utilis/methods/voiceAssistant/patientSuggestion";
import { useEmrPatientServiceContext } from "../../../utilis/methods/voiceAssistant/useEmrPatientServiceContext";
import PatientSuggestionContent from "./PatientSuggestionContent";

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function EmrPatientSuggestionModal({ open, onClose }: Props) {
  const { showSnackbar } = usePopup();
  const { hasPatientContext, buildPatientServicePayload, resolvedEncounterId } =
    useEmrPatientServiceContext();
  const [suggestion, setSuggestion] = useState<PatientSuggestionResponse | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const { mutate: fetchSuggestions, isLoading } = useMutation(voicePromptService, {
    onSuccess: (result) => {
      if (result.responseIndicator !== "success") {
        showSnackbar({
          open: true,
          message: result.responseMessage || "Could not load patient suggestions.",
          variant: "warning",
          duration: 5000,
        });
        return;
      }
      const parsed = parsePatientSuggestionResponse(result);
      setSuggestion(parsed);
      setHasFetched(true);
    },
    onError: (error: {
      message?: string;
      response?: { data?: { responseMessage?: string } };
    }) => {
      showSnackbar({
        open: true,
        message:
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Failed to load patient suggestions.",
        variant: "error",
        duration: 5000,
      });
    },
  });

  const loadSuggestions = useCallback(() => {
    if (!hasPatientContext) return;
    fetchSuggestions(
      buildPatientServicePayload("suggestion", PATIENT_SUGGESTION_PROMPT),
    );
  }, [buildPatientServicePayload, fetchSuggestions, hasPatientContext]);

  useEffect(() => {
    if (!open) return;
    setSuggestion(null);
    setHasFetched(false);
    if (hasPatientContext) {
      loadSuggestions();
    }
  }, [hasPatientContext, loadSuggestions, open, resolvedEncounterId]);

  if (!hasPatientContext) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
          AI patient suggestions
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {dayjs().format("DD MMM YYYY")}
        </Typography>
        <IconButton
          size="small"
          aria-label="Refresh suggestions"
          onClick={loadSuggestions}
          disabled={isLoading}
          sx={{ color: "inherit" }}
        >
          {isLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <RefreshIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label="Close suggestions"
          onClick={onClose}
          sx={{ color: "inherit" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh", bgcolor: "#f8fafc", p: 1.5 }}>
        {isLoading && !hasFetched ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Generating patient suggestions…
            </Typography>
          </Box>
        ) : !hasSuggestionContent(suggestion) ? (
          <Alert severity="info" sx={{ py: 0.5 }}>
            No suggestions available for this patient right now.
          </Alert>
        ) : (
          <PatientSuggestionContent suggestion={suggestion!} />
        )}
      </DialogContent>
    </Dialog>
  );
}
